import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash-es';
import { uploadResetAction, uploadedVideoSelector, getUploadVideoObjectSelector } from '../index';
import { appRoutes } from 'routes';
import { reduxNotificationAction } from 'components/redux-notification/actions';
import { REDUX_NOTIFICATION_TYPES } from 'components/redux-notification';
import { ContentBox } from 'components/content-box';
import { ButtonContainer, Button } from 'components/button';
import { VideoItemThumbnail } from 'components/video-generating';
import { history } from 'app.history';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';

const podcastLabels = {
  assetAvailable: 'your_podcast_will_be_available_shortly',
  viewUploaded: 'view_uploaded_podcasts',
  uploadAnother: 'upload_another_podcast',
};

const videoLabels = {
  assetAvailable: 'your_video_will_be_available_shortly',
  viewUploaded: 'view_uploaded_videos',
  uploadAnother: 'upload_another_video',
};

const UploadedVideosSummary = ({ labels }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const uploadedVideos = useSelector(uploadedVideoSelector);

  const handleCopyLink = (text) => {
    dispatch(reduxNotificationAction(text, true, 3, REDUX_NOTIFICATION_TYPES.SUCCESS));
  };

  return (
    <div className="form-group">
      <p className="upload-completed-label">{t(labels.assetAvailable)}</p>
      <div className="uploaded-videos">
        {uploadedVideos.map((v, i) => (
          <div className="uploaded-videos__item" key={i}>
            <div className="item-link">
              <a
                target="_blank"
                className="video-link"
                data-e2e="video-link"
                rel="noopener noreferrer"
                href={v.url}
                alt={v.channelName}
              >
                {v.channelName}
              </a>
            </div>
            <div className="item-copy">
              <CopyToClipboard text={v.url} onCopy={() => handleCopyLink(t('copy_link_success'))}>
                <span>
                  <FontAwesomeIcon icon={faCopy} />
                  {t('copy_link')}
                </span>
              </CopyToClipboard>
            </div>
          </div>
        ))}
      </div>
      <small className="form-text">
        <FontAwesomeIcon className="info" icon={faInfoCircle} />

        {t(labels.viewUploaded)}
        <Link className="my-videos-link" to={'/my-videos'}>
          {t('my_uploads')}
        </Link>
      </small>
    </div>
  );
};

export const UploadCompleted = ({ isPodcast }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const uploadedVideo = useSelector(getUploadVideoObjectSelector);
  const labels = isPodcast ? podcastLabels : videoLabels;

  const handleUploadAgain = () => {
    dispatch(uploadResetAction());
    history.push(appRoutes.upload);
  };

  const handleBackToHome = () => history.push(appRoutes.homePage);

  const videoId = get(uploadedVideo, 'video.id');

  return (
    <div className="ct-upload-completed">
      <ContentBox>
        <h2 className="title">
          {t('upload_complete')}
          <FontAwesomeIcon icon={faCheck} className="completed" />
        </h2>
        <div className="ct-upload-completed__form">
          <div className="ct-upload-completed__thumbnail">
            <VideoItemThumbnail video={get(uploadedVideo, 'video')} url={`/video/${videoId}#top`} />
          </div>
          <UploadedVideosSummary labels={labels} />
        </div>

        <div className="ct-upload-completed__buttons">
          <ButtonContainer left={true}>
            <Button onClick={handleBackToHome}>{t('back_to_home_page')}</Button>
            <Button onClick={handleUploadAgain} secondary={true}>
              {t(labels.uploadAnother)}
            </Button>
          </ButtonContainer>
        </div>
      </ContentBox>
    </div>
  );
};
